import { useEffect, useState } from "react";
import { usePrintReady } from "@/lib/print";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, shopLogoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const digits = (s) => (s || "").replace(/\D/g, "");
function fmtDoc(d, type) {
  const s = digits(d);
  if (type === "pj" && s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  if (type === "pf" && s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  return d || "—";
}
function fmtCep(c) { const s = digits(c); return s.length === 8 ? `${s.slice(0,5)}-${s.slice(5)}` : (c || "—"); }
function fmtPhone(p) { const s = digits(p); if (!s) return "—"; if (s.length <= 10) return s.replace(/^(\d{2})(\d{4})(\d{0,4})$/, "($1) $2-$3"); return s.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3"); }
function fmtDate(d) { if (!d) return "—"; try { return new Date(d + "T00:00:00").toLocaleDateString("pt-BR"); } catch { return d; } }

export default function CustomerSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cust, setCust] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  usePrintReady(loading ? "loading" : cust ? "ready" : "error", "Cliente não encontrado");

  useEffect(() => {
    (async () => {
      try {
        const [me, list, vs] = await Promise.all([
          api.get("/auth/me"),
          api.get("/customers"),
          api.get(`/customers/${id}/vehicles`).catch(() => ({ data: [] })),
        ]);
        setShop(me.data.shop || null);
        const c = (list.data || []).find((x) => x.id === id);
        setCust(c || null);
        setVehicles(vs.data || []);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white text-zinc-800">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
  if (!cust) return (
    <div className="min-h-screen flex items-center justify-center bg-white text-zinc-800">
      <div className="text-center">
        <p>Cliente não encontrado.</p>
        <Button onClick={() => navigate(-1)} className="mt-3">Voltar</Button>
      </div>
    </div>
  );

  const addr = [cust.street, cust.number, cust.complement].filter(Boolean).join(", ")
    + (cust.neighborhood ? ` — ${cust.neighborhood}` : "")
    + (cust.city ? `. ${cust.city}` : "")
    + (cust.uf ? `/${cust.uf}` : "");

  return (
    <div className="min-h-screen bg-white text-zinc-900 print:bg-white">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="no-print bg-zinc-100 border-b border-zinc-200 px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-zinc-700 hover:bg-zinc-200" data-testid="sheet-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <div className="flex-1" />
        <Button onClick={() => window.print()} className="bg-zinc-900 text-white hover:bg-zinc-800" data-testid="sheet-print">
          <Printer className="h-4 w-4 mr-1" /> Imprimir
        </Button>
      </div>
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <header className="flex items-center justify-between border-b border-zinc-300 pb-6 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Ficha do cliente</div>
            <h1 className="text-3xl font-black uppercase mt-1">{cust.name}</h1>
            {cust.person_type === "pj" && cust.legal_name && <p className="text-sm text-zinc-500 mt-1">{cust.legal_name}</p>}
          </div>
          {shop?.logo_path ? (
            <img src={shopLogoUrl(shop.id)} alt={shop.name} className="h-16 w-auto max-w-[160px] object-contain" />
          ) : (
            <div className="text-right">
              <div className="font-black uppercase">{shop?.name || "AutoOficina"}</div>
              <div className="text-xs text-zinc-500">AutoOficina</div>
            </div>
          )}
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Tipo" value={cust.person_type === "pj" ? "Pessoa Jurídica" : "Pessoa Física"} />
          <Row label={cust.person_type === "pj" ? "CNPJ" : "CPF"} value={fmtDoc(cust.document, cust.person_type)} />
          {cust.person_type === "pj" && <Row label="Nome fantasia" value={cust.trading_name || "—"} />}
          {cust.person_type === "pf" && <Row label="Nascimento" value={fmtDate(cust.birth_date)} />}
          <Row label="E-mail" value={cust.email || "—"} />
          <Row label="Telefone" value={fmtPhone(cust.phone)} />
          <Row label="CEP" value={fmtCep(cust.cep)} />
          <Row label="UF / Cidade" value={[cust.uf, cust.city].filter(Boolean).join(" / ") || "—"} />
          <div className="sm:col-span-2"><Row label="Endereço" value={addr || "—"} /></div>
        </section>

        {vehicles.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Veículos</h2>
            <table className="w-full text-sm border-t border-zinc-300">
              <thead><tr className="text-left text-zinc-500 text-xs uppercase">
                <th className="py-2">Placa</th><th>Marca</th><th>Modelo</th><th>Ano</th><th>Cor</th>
              </tr></thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.plate} className="border-t border-zinc-200">
                    <td className="py-2 font-mono">{v.plate}</td>
                    <td>{v.make || "—"}</td>
                    <td>{v.model || "—"}</td>
                    <td>{[v.year, v.year_model].filter(Boolean).join("/") || "—"}</td>
                    <td>{v.color || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <footer className="mt-10 text-xs text-zinc-500 border-t border-zinc-200 pt-4">
          Emitido em {new Date().toLocaleString("pt-BR")} — {shop?.name || "AutoOficina"}
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-zinc-900">{value}</div>
    </div>
  );
}
